import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, MapPin } from 'lucide-react';
import { useLang } from '../i18n';
import { useSeason } from '../lib/useSeasons';
import Seo from '../components/Seo';
import { Reveal } from '../components/Reveal';
import { ProductTile } from '../components/ProductTile';
import { FacetRow } from '../components/FacetRow';
import { FALLBACK_IMAGE as FALLBACK } from '../lib/api';
import { Img } from '../components/Img';
import NotFound from './NotFound';
import { LoopVideo } from '../components/LoopVideo';
import { ScrubWords } from '../components/ScrubWords';
import { TavolaSiApparecchia } from '../components/season/TavolaSiApparecchia';
import { SeasonGallery } from '../components/season/SeasonGallery';
import { seasonMedia, altOf } from '../lib/seasonMedia';
import {
  alterna,
  categorieDi,
  curaIdee,
  curaPezzi,
  estraiIdee,
  registro,
  tavolaNatale,
  usePezziTavolaNatale,
} from '../lib/seasonCuration';

/**
 * La vetrina d'apertura.
 *
 * Quando arrivera' una foto ambientata prende il suo posto (`photo` nel
 * registro, il file in `public/assets/stagioni/`). Finche' non c'e', un pezzo
 * solo, grande, su carta calda: tre pacchetti affiancati su telefono venivano
 * 100px l'uno e sembravano una striscia di miniature, non un'apertura. Il
 * filetto in basso e' il colore della stagione, e il nome sotto dice cos'e' —
 * come la didascalia di una foto.
 */
const Vetrina = ({ pezzo, photo, alt }) => {
  const { t } = useLang();
  if (!pezzo && !photo) return null;

  return (
    <figure className="relative bg-carta-2">
      {photo ? (
        <img src={photo} alt={alt} className="aspect-[4/3] w-full object-cover md:aspect-[21/9]" />
      ) : (
        <Link
          to={`/prodotti/${pezzo.slug}`}
          data-testid="season-showcase"
          className="group block px-[var(--gutter)] py-12 md:py-20"
        >
          <Img
            src={pezzo.images?.[0] || FALLBACK}
            alt={pezzo.title}
            sizes="(max-width:768px) 78vw, 460px"
            onError={(e) => {
              e.currentTarget.src = FALLBACK;
            }}
            className="img-zoom mx-auto w-[78%] max-w-[300px] object-contain mix-blend-multiply md:max-w-[460px]"
          />
        </Link>
      )}
      <span
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-[3px]"
        style={{ background: 'var(--accento)' }}
      />
      {!photo && (
        <figcaption className="wrap pb-7 pt-1 text-center text-sm text-inchiostro/65">
          {t('season.inThePhoto')}:{' '}
          <Link to={`/prodotti/${pezzo.slug}`} className="link-ul text-inchiostro">
            {pezzo.title.replace(/,\s*[^,]+$/, '')}
          </Link>
        </figcaption>
      )}
    </figure>
  );
};

/**
 * L'apertura cinematografica: la foto vera con Ken Burns CSS (spento con
 * reduced motion, pausa col pulsante), a tutta altezza su notte. La seconda
 * meta' del claim va in ocra: sul buio il marsala non regge il contrasto.
 */
const HeroCinema = ({ s, hero, preview }) => {
  const { t, lang } = useLang();
  const claim = s.claim.split(',');
  return (
    <header
      data-testid="season-hero"
      className="relative flex h-[92svh] min-h-[560px] items-end overflow-hidden bg-notte text-carta"
    >
      <LoopVideo
        src={hero.src}
        srcMobile={hero.srcMobile}
        poster={hero.poster}
        width={hero.width}
        height={hero.height}
        label={altOf(hero, lang)}
        priority
        testid="season-hero-video"
        className="absolute inset-0"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-notte/70"
      />
      <div className="wrap relative pb-14 md:pb-20">
        <h1 className="max-w-[14ch] text-[clamp(2.75rem,9.5vw,7rem)] leading-[0.92]">
          {claim[0]}
          <span className="accento block text-ocra">{claim[1] || ''}</span>
        </h1>
        <p className="label-wide mt-6 flex flex-wrap items-center gap-3 text-carta/80">
          {s.kicker}
          {!s.active && preview && (
            <span data-testid="season-preview-badge" className="border border-carta/60 px-2 py-1 text-[14px]">
              {t('season.preview')}
            </span>
          )}
        </p>
        <p className="prosa mt-8 max-w-[54ch] text-base !text-carta/85 md:text-lg">{s.lead}</p>
      </div>
      <span aria-hidden className="absolute inset-x-0 bottom-0 h-[3px]" style={{ background: 'var(--accento)' }} />
    </header>
  );
};

/** Un'idea: il numero, la frase, e i pezzi che la mettono in pratica. */
const Idea = ({ titolo, testo, pezzi }) => {
  const { t } = useLang();
  return (
    <li>
      <div className="wrap grid gap-8 py-11 md:grid-cols-[0.9fr_1.1fr] md:gap-16 md:py-16">
        <div>
          <h3 className="mt-3 max-w-[20ch] text-[clamp(1.7rem,5.4vw,2.8rem)] leading-[1.06]">
            {titolo}
          </h3>
          <p className="prosa mt-4 max-w-[44ch]">{testo}</p>
        </div>

        {/* Su telefono i tre pezzi si scorrono col dito e il nome si legge; in
            tre colonne da 100px sarebbe stato un francobollo con la didascalia
            sotto. Da tablet in su stanno in fila senza scorrimento. */}
        <ul className="-mx-[var(--gutter)] flex snap-x snap-mandatory gap-4 overflow-x-auto px-[var(--gutter)] pb-1 md:mx-0 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:px-0 fila fila-margini">
          {pezzi.map((p) => (
            <li key={p.sku} className="w-[46%] shrink-0 snap-start sm:w-[32%] md:w-auto">
              <ProductTile product={p} />
            </li>
          ))}
        </ul>
        {!pezzi.length && <p className="text-sm text-inchiostro/65">{t('common.empty')}</p>}
      </div>
    </li>
  );
};

/**
 * La pagina di una stagione.
 *
 * Non c'e' niente di scritto a mano qui dentro: titolo, racconto, idee e pezzi
 * arrivano dal registro delle stagioni e dalle sue regole sul catalogo. Il
 * colore e' una variabile CSS, cosi' cambia con la stagione senza classi
 * condizionali sparse.
 */
export default function Season() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { data: s, isLoading, isError, preview } = useSeason(slug);
  const [categoria, setCategoria] = useState(null);
  const [shown, setShown] = useState(8);
  const media = seasonMedia(slug);

  // Le idee con la foto (Natale) pescano dal pool Natale-Tavola, a sorte a ogni
  // caricamento: l'estrazione e' fissata finche' la pagina e' montata. Finche' il
  // pool non arriva, o se non risponde, restano i pezzi Tavola del backend.
  const { data: pool } = usePezziTavolaNatale(!!media?.ideas, lang);
  const scelte = useMemo(() => estraiIdee(pool, Math.random, media?.ideas?.length), [pool, media]);

  // Una pagina, un registro: un pezzo o una foto compare una volta sola, prima
  // nelle idee e poi, se non c'e' gia', tra i pezzi delle feste. Il filtro
  // offre solo le categorie dei pezzi davvero arrivati.
  const pagina = useMemo(() => {
    const prendi = registro();
    const idee = curaIdee(s?.ideas || [], scelte, prendi, 3, media ? tavolaNatale : undefined);
    const pezzi = curaPezzi(s?.products || [], prendi);
    // il filtro lavora sulla categoria del feed; in inglese si mostra category_label
    const etichette = Object.fromEntries(pezzi.filter((p) => p.category_label)
      .map((p) => [p.category.trim().toLowerCase(), p.category_label.trim().toLowerCase()]));
    return { idee, pezzi, categorie: categorieDi(pezzi), etichette };
  }, [s, media, scelte]);

  const prodotti = useMemo(() => {
    if (!categoria) return pagina.pezzi;
    return alterna(pagina.pezzi.filter((p) => (p.category || '').toLowerCase() === categoria.toLowerCase()));
  }, [pagina, categoria]);

  useEffect(() => setShown(8), [categoria, slug]);

  if (isError) return <NotFound />;
  if (isLoading || !s) {
    return (
      <div className="wrap py-32">
        <p className="font-display text-3xl text-inchiostro/40">{t('common.loading')}</p>
      </div>
    );
  }

  const claim = s.claim.split(',');
  const vetrina = (s.products || [])[0];

  return (
    <div style={{ '--accento': s.accent }}>
      <Seo
        title={s.kicker}
        description={s.lead}
        image={media?.storie?.src || s.photo || vetrina?.images?.[0]}
        path={`/stagioni/${s.slug}`}
      />

      {media?.hero ? (
        <HeroCinema s={s} hero={media.hero} preview={preview} />
      ) : (
        /* ─── apertura: carta calda, il colore della stagione come firma ─── */
        <header
          data-testid="season-hero"
          className="border-t-[5px] bg-carta-2"
          style={{ borderTopColor: 'var(--accento)' }}
        >
          <div className="wrap pb-11 pt-12 md:pb-16 md:pt-16">
            <h1 className="text-[clamp(2.5rem,8.6vw,6rem)] leading-[0.94]">
              {claim[0]}
              <span className="accento block" style={{ color: 'var(--accento)' }}>
                {claim[1] || ''}
              </span>
            </h1>
            <p className="label-wide mt-6 flex flex-wrap items-center gap-3 text-inchiostro/65">
              {s.kicker}
              {!s.active && preview && (
                <span
                  data-testid="season-preview-badge"
                  className="border border-inchiostro/55 px-2 py-1 text-[14px]"
                >
                  {t('season.preview')}
                </span>
              )}
            </p>

            <p className="prosa mt-8 max-w-[54ch] pt-7 text-base md:text-lg">
              {s.lead}
            </p>
          </div>

          <Vetrina pezzo={vetrina} photo={s.photo} alt={s.kicker} />
        </header>
      )}

      {/* ─── il racconto: un paragrafo, grande, e basta ─── */}
      <section data-testid="season-story" className={`wrap ${media ? 'py-16 md:py-24' : 'py-14 md:py-20'}`}>
        {media ? (
          <ScrubWords
            text={s.story}
            className="max-w-[30ch] font-display text-[clamp(1.8rem,4.6vw,3.6rem)] leading-[1.08] tracking-[-0.02em]"
          />
        ) : (
          <Reveal>
            <p className="prosa max-w-[58ch] text-lg md:text-xl">{s.story}</p>
          </Reveal>
        )}
      </section>

      {/* ─── le idee: quello che si fa, coi pezzi che servono ─── */}
      {!!pagina.idee.length && media?.ideas?.length === pagina.idee.length && (
        <TavolaSiApparecchia title={s.ideas_title} ideas={pagina.idee} foto={media.ideas} />
      )}
      {!!pagina.idee.length && media?.ideas?.length !== pagina.idee.length && (
        <section data-testid="season-ideas" className="pb-4">
          <div className="wrap">
            <h2 className="max-w-[26ch] text-[clamp(2rem,5.6vw,3.8rem)] leading-[1.04]">
              {s.ideas_title}
            </h2>
          </div>
          <ol className="mt-9">
            {pagina.idee.map((idea) => (
              <Idea key={idea.title} titolo={idea.title} testo={idea.text} pezzi={idea.products} />
            ))}
          </ol>
        </section>
      )}

      {media?.gallery && <SeasonGallery items={media.gallery} />}

      {/* ─── le referenze della stagione ─── */}
      <section data-testid="season-products" className="py-14 md:py-20">
        <div className="wrap">
          <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
            <h2 className="text-[clamp(2rem,5.6vw,3.8rem)] leading-[1.04]">{s.pieces_title}</h2>
            <span className="label-wide text-inchiostro/65">{prodotti.length}</span>
          </div>

          {/* Le categorie erano etichette che sembravano filtri e non filtravano:
              o si toccano e succede qualcosa, o non ci sono. */}
          {pagina.categorie.length > 1 && (
            <div className="mt-7 pt-6">
              <FacetRow
                label={t('common.category')}
                items={[t('season.allPieces'), ...pagina.categorie.map(([name]) => name)]}
                active={categoria || t('season.allPieces')}
                onPick={(v) => setCategoria(v === t('season.allPieces') ? null : v)}
                display={(v) => {
                  const e = pagina.etichette[v.toLowerCase()];
                  return e ? e.charAt(0).toUpperCase() + e.slice(1) : v;
                }}
                testid="season-facet"
              />
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-10 md:grid-cols-3 lg:grid-cols-4">
            {prodotti.slice(0, shown).map((p, i) => (
              <Reveal key={p.sku} delay={Math.min(i, 7) * 0.04} y={14}>
                <ProductTile product={p} />
              </Reveal>
            ))}
          </div>

          {shown < prodotti.length && (
            <div className="mt-12 text-center">
              <button
                type="button"
                onClick={() => setShown((n) => n + 8)}
                data-testid="season-show-more"
                className="btn btn-line"
              >
                {t('common.more')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ─── chiusura: la festa continua sulla tavola di chi legge ─── */}
      <section
        data-testid="season-closing"
        className={`relative overflow-hidden bg-inchiostro text-carta ${media?.closing ? 'py-20 md:py-32' : 'py-14 md:py-20'}`}
      >
        {media?.closing && (
          <>
            <LoopVideo
              src={media.closing.src}
              srcMobile={media.closing.srcMobile}
              poster={media.closing.poster}
              width={media.closing.width}
              height={media.closing.height}
              testid="season-closing-video"
              className="absolute inset-0"
            />
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-inchiostro/75" />
          </>
        )}
        <div className="wrap relative grid gap-9 lg:grid-cols-[1.1fr_0.9fr] lg:items-end lg:gap-20">
          <div>
            <h2 className="max-w-[20ch] text-[clamp(2rem,5.6vw,3.8rem)] leading-[1.04]">
              {t('season.closingTitle')}
            </h2>
            <p className="prosa mt-5 max-w-[46ch] !text-carta/70">{t('season.closingText')}</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/componi" data-testid="season-cta-build" className="btn btn-line-light">
              {t('nav.build')}
              <ArrowUpRight size={15} strokeWidth={2} />
            </Link>
            <Link to="/dove-comprare" data-testid="season-cta-where" className="btn btn-solid">
              <MapPin size={13} strokeWidth={2.2} />
              {t('common.findRetailer')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
